import React, { useCallback, useContext, useMemo, useState } from 'react'
import BulkReviewsContext, { type BulkReviewsContextType } from '../context';
import CategoryCompressed from './CategoryCompressed';
import { Button } from 'react-bootstrap';
import CategoryCustom from './CategoryCustom';
import type { ReviewListProps, SupplierReview } from '../types';
import { INITIAL_CATEGORY_COUNT } from '../constants';

const ReviewList = ({ currentSupplierId }: ReviewListProps) => {
    const { categoryInfo, reviews, showCount, setShowCount } = useContext<BulkReviewsContextType>(BulkReviewsContext);

    if (!categoryInfo?.categories) {
        return null;
    }

    const prepopulatedReviews = useMemo(() => {
        return currentSupplierId
            ? reviews.filter(f => f.prepopulated && f.supplierId === currentSupplierId)
            : reviews.filter(f => f.prepopulated);
    }, [reviews, currentSupplierId]);

    const allCategories = useMemo(() => {
        const filteredCategories = currentSupplierId
        ? categoryInfo.categories.filter(cat => {
              const review = reviews.find(r => r.supplierCategoryId === cat.categoryId && r.supplierId === currentSupplierId);
              return !!review || prepopulatedReviews.some(p => p.supplierCategoryId === cat.categoryId);
          })
        : categoryInfo.categories;

        return filteredCategories.sort((a, b) => {
            const aReview = prepopulatedReviews.find(p => p.supplierCategoryId === a.categoryId);
            const bReview = prepopulatedReviews.find(p => p.supplierCategoryId === b.categoryId);

            const orderItems = () => {
                const aPrepopulated = !!aReview;
                const bPrepopulated = !!bReview;

                // Sort by prepopulated (true comes first)
                if (!!aPrepopulated !== !!bPrepopulated) {
                    return aPrepopulated ? -1 : 1;
                }

                return a.categoryOrder - b.categoryOrder;
            };

            return orderItems();
        });
    }, [prepopulatedReviews, currentSupplierId, categoryInfo.categories]);

    const supplierSort = useCallback((a: SupplierReview, b: SupplierReview) => {
        if (a.supplierId === currentSupplierId && b.supplierId !== currentSupplierId) {
            return -1;
        }
        if (a.supplierId !== currentSupplierId && b.supplierId === currentSupplierId) {
            return 1;
        }
        return a.id - b.id;
    }, [currentSupplierId]);

    let count = 0;
    let allShown = true;

    return (
        <>
            {allCategories.map((cat, ind) => {
                const catReviews = reviews.filter(
                    r => r.supplierCategoryId === cat.categoryId && !r.custom && (!currentSupplierId || r.supplierId === currentSupplierId)
                );

                if (count < showCount || count < prepopulatedReviews.length) {
                    if (catReviews.length > 0) {
                        count = count + catReviews.length;
                        return catReviews.sort(supplierSort).map(rev => <CategoryCompressed key={`${ind}-${rev.id}`} category={cat} review={rev} />);
                    } else {
                        count++;
                        return <CategoryCompressed key={`${ind}-${0}`} category={cat} />;
                    }
                } else {
                    allShown = false;
                }
                return null;
            })}
            {!allShown && <Button key={`btn-${count}`} className="btn-loading" variant={'outline-secondary'} data-testid="load-more-button" onClick={() => !!setShowCount && setShowCount(showCount + INITIAL_CATEGORY_COUNT)}>+ Load More Categories</Button>}
            {reviews.filter(r => r.custom && (!currentSupplierId || r.supplierId === currentSupplierId)).sort((a, b) => a.id - b.id).map(rv => {
                const category = categoryInfo?.categories?.find(c => c.categoryId === rv.supplierCategoryId);
                return (!!category ? <CategoryCompressed key={`custom-${rv.id}`} category={category} review={rv} /> : null);
            })}
            {!currentSupplierId && <CategoryCustom />}
        </>
    )
}

export default ReviewList